<template>
  <v-card class="pa-8 mt-4">
    <v-card-title class="mb-4 d-flex align-center">
      <v-btn
        class="me-4"
        color="primary"
        size="small"
        icon="fa-solid fa-circle-4"
      />
      <span>
        Setores
      </span>
    </v-card-title>
    <v-row v-if="sectorStates.length === 0">
      <v-col>
        <span class="text-medium-emphasis">Nenhum setor encontrado para este evento.</span>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col
        v-for="state in sectorStates"
        :key="state.sector_id"
        sm="4"
        lg="3"
        class="d-flex flex-column ga-3"
      >
        <div class="text-subtitle-1">
          {{ state.sector_name }}
        </div>
        <div>
          <v-textarea
            rows="1"
            :model-value="state.description ?? ''"
            variant="outlined"
            label="Descrição"
            @update:model-value="(value) => handleDescriptionChange(value, state)"
          />
        </div>
        <card-image-picker
          :model-value="state.shieldImageFile"
          :preview-url="state.previewUrl ?? undefined"
          label=""
          :dimensions="{ width: 150, height: 150 }"
          @update:model-value="(file) => handleImageUpdate(file, state)"
        />
        <div class="d-flex flex-column">
          <v-checkbox
            color="primary"
            hide-details
            :model-value="state.color === null"
            @update:model-value="(checked) => handleColorCheckbox(checked, state)"
          >
            <template #label>
              <span class="text-subtitle-2">
                Manter a cor primária do produtor
              </span>
            </template>
          </v-checkbox>
          <v-color-picker
            :model-value="state.color"
            class="w-100"
            :class="{ 'opacity-50': state.color === null }"
            :modes="['hex']"
            :disabled="state.color === null"
            @update:model-value="(color) => handleColorChange(color, state)"
          />
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts" setup>
import CardImagePicker from '@/components/form/CardImagePicker.vue'
import Event from '@/types/Event'
import Sector from '@/types/Sector'
import { ref, watch } from 'vue'

export interface SectorConfigState {
  sector_id: string
  sector_name: string
  shieldImageFile: File | undefined
  previewUrl: string | null
  dirty: boolean
  color: string | null
  description?: string
}

interface Props {
  event: Event
  sectors: Sector[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [SectorConfigState[]]
}>()

const sectorStates = ref<SectorConfigState[]>([])

watch(
  () => props.sectors,
  (sectors) => {
    sectorStates.value = sectors.map((sector) => {
      const existing = props.event.sector_configs?.find(c => c.sector_id === sector.id)
      return {
        sector_id: sector.id,
        sector_name: sector.name,
        shieldImageFile: undefined,
        previewUrl: getImageURLFromId(existing?.shield_image_id),
        dirty: false,
        color: existing?.color ?? null,
        description: existing?.description,
        active: sector.active,
      }
    }).filter(s => s.active)
  },
  { immediate: true },
)

function getImageURLFromId(id?: string | null) {
  if (!id) return null
  return props.event.images?.find(i => i.id === id)?.url ?? null
}

watch(sectorStates, (value) => {
  emit('update:modelValue', value)
}, { deep: true })

function handleImageUpdate(file: File | undefined, state: SectorConfigState) {
  state.shieldImageFile = file
  state.previewUrl = null
  state.dirty = true
}

function handleColorChange(color: string, state: SectorConfigState) {
  state.color = color
  state.dirty = true
}

function handleDescriptionChange(description: string, state: SectorConfigState) {
  state.description = description
  state.dirty = true
}

function handleColorCheckbox(checked: boolean | null, state: SectorConfigState) {
  state.color = checked ? null : '#EA8300'
  state.dirty = true
}
</script>

<style lang="scss">
.v-color-picker-edit__input span {
  display: none
}
</style>
